import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { authAPI } from '../services/api'
import { useAuthStore } from '../store/authStore'
import Spinner from '../components/Spinner'
import AuthShell, {
  authButtonClass,
  authInputClass,
  authLabelClass,
  authLinkClass,
  authSpinnerClass,
} from '../components/auth/AuthShell'

const Register = () => {
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [tosAccepted, setTosAccepted] = useState(false)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const setAuth = useAuthStore((state) => state.setAuth)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validation
    if (password !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters long')
      return
    }

    if (!tosAccepted) {
      toast.error('You must accept the Terms of Service to continue')
      return
    }

    setLoading(true)

    try {
      // Registration returns an immediately-usable access/refresh token pair,
      // avoiding a redundant login round trip.
      const data = await authAPI.register({
        email,
        username,
        password,
        full_name: fullName || undefined,
        tos_accepted: true,
      })

      // Strip the token fields out of the response so only the user
      // profile data is stored as `user` in the auth store.
      const { access_token, refresh_token, token_type, ...user } = data
      setAuth(access_token, user, refresh_token)

      toast.success('Registration successful!')
      navigate('/')
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Registration failed. Please try again.'
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell heading="Create your account">
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="email" className={authLabelClass}>
            Email Address
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={authInputClass}
            required
          />
        </div>

        <div>
          <label htmlFor="username" className={authLabelClass}>
            Username
          </label>
          <input
            id="username"
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className={authInputClass}
            required
          />
        </div>

        <div>
          <label htmlFor="fullName" className={authLabelClass}>
            Full Name <span className="font-normal text-gray-500">(Optional)</span>
          </label>
          <input
            id="fullName"
            type="text"
            autoComplete="name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className={authInputClass}
          />
        </div>

        <div>
          <label htmlFor="password" className={authLabelClass}>
            Password
          </label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={authInputClass}
            required
            minLength={8}
          />
        </div>

        <div>
          <label htmlFor="confirmPassword" className={authLabelClass}>
            Confirm Password
          </label>
          <input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={authInputClass}
            required
            minLength={8}
          />
        </div>

        <div className="flex items-start gap-3">
          <input
            id="tos"
            type="checkbox"
            checked={tosAccepted}
            onChange={(e) => setTosAccepted(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-green-500"
            required
          />
          <label htmlFor="tos" className="cursor-pointer text-sm text-gray-300">
            I have read and agree to the{' '}
            <Link to="/terms" target="_blank" className={`${authLinkClass} underline`}>
              Terms of Service
            </Link>
            , including the Creator Regulatory Compliance obligations in Section 11. I understand
            that I am solely responsible for complying with applicable investment adviser and
            securities laws in my jurisdiction.
          </label>
        </div>

        <button type="submit" disabled={loading || !tosAccepted} className={authButtonClass}>
          {loading && <Spinner size="sm" className={authSpinnerClass} />}
          {loading ? 'Creating account' : 'Sign up'}
        </button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-sm text-gray-400">
          Already have an account?{' '}
          <Link to="/login" className={authLinkClass}>
            Sign in
          </Link>
        </p>
      </div>
    </AuthShell>
  )
}

export default Register
